import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import * as req from "@script/req";

export interface PrevTailNurseAssignment {
  reason: string;
  target_group_id?: string;
  target_group_name?: string;
  target_schedule_id?: string | null;
  target_shifts?: Record<string, string | null>;
  start_date: string;
  end_date: string | null;
  start_day?: number;
  end_day?: number | null;
}

export interface PrevTailNurse {
  nurse_id: string;
  name: string;
  shifts: Record<string, string | null>;
  assignments?: PrevTailNurseAssignment[];
}

export interface PrevTailData {
  schedule_id?: string;
  schedule_name?: string;
  schedule_status?: string;
  nurses: PrevTailNurse[];
  tail_days: number[];
  prev_month?: number;
  prev_year?: number;
}

export interface UseRosterPrevTailOptions {
  year: number;
  month: number;
  scheduleId?: string | null;
  tailDays?: number; // 기본값 6일 (1~15 범위)
  groupId?: string | null;
  enabled?: boolean;
}

/**
 * 전월 말일 근무표 데이터를 가져오는 커스텀 훅
 *
 * @param options - 옵션 객체
 * @param options.year - 현재월 연도 (예: 2026)
 * @param options.month - 현재월 월 (예: 3)
 * @param options.scheduleId - 현재월 근무표 ID (선택, 제공 시 해당 근무표 소속 간호사 기준)
 * @param options.tailDays - 전월에서 가져올 일수 (기본 6일, 1~15 범위)
 * @param options.groupId - 그룹 ID (admin만 필요, 수간호사는 토큰에서 자동 처리)
 * @param options.enabled - 쿼리 활성화 여부 (기본 true)
 *
 * @returns useQuery 결과
 * - data: null이면 전월 근무표가 없음을 의미 (전월 영역 미표시 또는 "전월 데이터 없음" 안내)
 * - 전월에 없던 신규 입사자는 shifts 값이 null
 */
export default function useRosterPrevTail({
  year,
  month,
  scheduleId,
  tailDays = 6,
  groupId,
  enabled = true,
}: UseRosterPrevTailOptions) {
  return useQuery<PrevTailData | null>({
    queryKey: QUERY_KEYS.roster.prevTail(
      year,
      month,
      scheduleId || undefined,
      tailDays,
      groupId || undefined
    ),
    queryFn: async () => {
      if (!year || !month) return null;

      const raw = await req.roster_prev_tail(year, month, {
        schedule_id: scheduleId || undefined,
        tail_days: tailDays,
        group_id: groupId || undefined,
      });

      if (!raw) return null;

      const payload = (raw as any).data ?? raw;
      const nurses = Array.isArray(payload?.nurses) ? payload.nurses : [];
      const tailDaysFromPayload = Array.isArray(payload?.tail_days)
        ? payload.tail_days
            .map(Number)
            .filter((d: number) => Number.isFinite(d))
        : [];

      const fallbackTailDays =
        nurses.length > 0
          ? Object.keys(nurses[0]?.shifts ?? {})
              .map(Number)
              .filter((d: number) => Number.isFinite(d))
              .sort((a: number, b: number) => a - b)
          : [];

      const nursesWithAssignments: PrevTailNurse[] = nurses.map((n: any) => ({
        ...n,
        assignments: Array.isArray(n.assignments)
          ? (n.assignments as PrevTailNurseAssignment[])
          : Array.isArray(n.nurse_assignments)
            ? (n.nurse_assignments as PrevTailNurseAssignment[])
            : [],
      }));

      return {
        schedule_id: payload?.schedule_id,
        schedule_name: payload?.schedule_name,
        schedule_status: payload?.schedule_status,
        nurses: nursesWithAssignments,
        tail_days: tailDaysFromPayload.length
          ? tailDaysFromPayload
          : fallbackTailDays,
        prev_month: payload?.prev_month,
        prev_year: payload?.prev_year,
      };
    },
    enabled: enabled && !!year && !!month,
    staleTime: 1000 * 60 * 5, // 5분간 캐시
  });
}
